import React, { useEffect, useMemo, useState } from 'react';
import { HiOutlineUserGroup } from 'react-icons/hi2';
import {
  ChatRequestsListModalOverlay,
  ChatRequestsListModalContent,
  ChatRequestModalHeader,
  ChatRequestModalTitle,
  ChatRequestModalCloseButton,
  CompactChatRequestsList,
  ChatRequestItem,
  ChatRequestItemInfo,
  ChatRequestItemName,
  ChatRequestItemConnectId,
  ChatRequestItemActions,
  ChatRequestActionButton,
  ChatRequestModalButtons,
  ChatRequestModalButton,
  EmptyRequestsMessage,
  ModalScrollBody,
  ConnectionIndicator,
} from '../../../styles/UserWorkspacePageStyles';
import {
  ModalFormInput,
  ModalFormStatus,
  ModalFormStatusMessage,
} from '../../../styles/ModalFormStyles';
import ContactSelectList from '../ContactSelectList';
import { techColors } from '../../../styles/TechRetroEffects';
import { useT } from '../../../LocaleContext';

const sectionTitleStyle = {
  fontSize: '0.75rem',
  color: '#888',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  margin: '14px 0 6px',
};

const infoCardStyle = {
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '6px',
  padding: '12px',
  background: 'rgba(255, 255, 255, 0.03)',
};

const statGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '8px',
  marginTop: '10px',
};

const statBoxStyle = {
  textAlign: 'center',
  padding: '8px 4px',
  borderRadius: '4px',
  background: 'rgba(0, 0, 0, 0.25)',
  border: '1px solid rgba(255, 255, 255, 0.06)',
};

const dangerZoneStyle = {
  marginTop: '16px',
  padding: '12px',
  borderRadius: '6px',
  border: '1px solid rgba(255, 0, 64, 0.35)',
  background: 'rgba(255, 0, 64, 0.05)',
};

const formatDate = (isoString) => {
  if (!isoString) {
    return '—';
  }
  try {
    return new Date(isoString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
};

const getMemberStatus = (member, t) => {
  const isOnline = member.user_online === 'true';
  return {
    label: isOnline ? t('common.online') : t('common.offline'),
    isOnline,
    isUnknown: false,
  };
};

const GroupInfoModal = ({
  isOpen,
  onClose,
  groupInfo,
  members,
  pendingInvites,
  isCreator,
  currentUserConnectId,
  onAddMembers,
  onRemoveMember,
  onLeaveGroup,
  onRenameGroup,
  onCancelInvite,
  onDeleteGroup,
  inviteContacts,
}) => {
  const t = useT();
  const [selectedInviteConnectIds, setSelectedInviteConnectIds] = useState([]);
  const [error, setError] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameError, setRenameError] = useState('');
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  const title = groupInfo?.title || t('chat.group');

  useEffect(() => {
    if (isOpen) {
      setRenameName(title);
      setRenameError('');
      setError('');
      setSelectedInviteConnectIds([]);
      setConfirmRemoveId(null);
      setShowAdminPanel(isCreator);
    }
  }, [isOpen, title, isCreator]);

  const onlineCount = useMemo(
    () => members.filter((m) => m.user_online === 'true').length,
    [members],
  );

  const showAdminDetails = isCreator;

  if (!isOpen) {
    return null;
  }

  const handleToggleInviteContact = (connectId) => {
    setSelectedInviteConnectIds((prev) => (
      prev.includes(connectId)
        ? prev.filter((id) => id !== connectId)
        : [...prev, connectId]
    ));
  };

  const handleAdd = async () => {
    setError('');
    if (selectedInviteConnectIds.length === 0) {
      setError('select at least one contact');
      return;
    }

    setIsAdding(true);
    try {
      await onAddMembers(selectedInviteConnectIds);
      setSelectedInviteConnectIds([]);
    } catch (addError) {
      setError(addError.message || 'failed to invite members');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRename = async () => {
    setRenameError('');
    const trimmed = renameName.trim();
    if (!trimmed) {
      setRenameError('group name is required');
      return;
    }
    if (trimmed === title.trim()) {
      return;
    }

    setIsRenaming(true);
    try {
      await onRenameGroup(trimmed);
    } catch (renameErr) {
      setRenameError(renameErr.message || 'failed to rename group');
    } finally {
      setIsRenaming(false);
    }
  };

  const handleRemoveClick = (connectId) => {
    if (confirmRemoveId === connectId) {
      onRemoveMember(connectId);
      setConfirmRemoveId(null);
      return;
    }
    setConfirmRemoveId(connectId);
  };

  return (
    <ChatRequestsListModalOverlay onClick={onClose}>
      <ChatRequestsListModalContent onClick={(e) => e.stopPropagation()}>
        <ChatRequestModalHeader>
          <div />
          <ChatRequestModalTitle>{t('modals.groupInfo')}</ChatRequestModalTitle>
          <ChatRequestModalCloseButton onClick={onClose}>
            ×
          </ChatRequestModalCloseButton>
        </ChatRequestModalHeader>

        <ModalScrollBody>
          <div style={infoCardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <HiOutlineUserGroup style={{ color: techColors.neonCyan, fontSize: '1.6em', flexShrink: 0 }} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: '1.05em', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {title}
                </div>
                {groupInfo?.creator_name && (
                  <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '2px' }}>
                    created by {groupInfo.creator_name}
                  </div>
                )}
              </div>
            </div>

            <div style={{
              ...statGridStyle,
              gridTemplateColumns: showAdminDetails && pendingInvites.length > 0
                ? 'repeat(3, 1fr)'
                : '1fr',
            }}
            >
              <div style={statBoxStyle}>
                <div style={{ fontSize: '1.1em', color: techColors.neonCyan }}>{members.length}</div>
                <div style={{ fontSize: '0.65rem', color: '#888', textTransform: 'uppercase' }}>{t('modals.members')}</div>
              </div>
              {showAdminDetails && (
                <div style={statBoxStyle}>
                  <div style={{ fontSize: '1.1em', color: techColors.textPrimary }}>{onlineCount}</div>
                  <div style={{ fontSize: '0.65rem', color: '#888', textTransform: 'uppercase' }}>{t('common.online')}</div>
                </div>
              )}
              {showAdminDetails && pendingInvites.length > 0 && (
                <div style={statBoxStyle}>
                  <div style={{ fontSize: '1.1em', color: '#ffaa00' }}>{pendingInvites.length}</div>
                  <div style={{ fontSize: '0.65rem', color: '#888', textTransform: 'uppercase' }}>{t('modals.pending')}</div>
                </div>
              )}
            </div>

            {showAdminDetails && (
              <div style={{ marginTop: '10px', fontSize: '0.75rem', color: '#888' }}>
                <span>created {formatDate(groupInfo?.created_at)}</span>
              </div>
            )}
          </div>

          <div style={sectionTitleStyle}>{t('modals.members')}</div>
          <CompactChatRequestsList>
            {members.length > 0 ? (
              members.map((member) => {
                const isSelf = member.connect_id === currentUserConnectId;
                const canRemove = isCreator && !member.is_creator && !isSelf;
                const canLeave = isSelf && !member.is_creator;
                const status = getMemberStatus(member, t);
                const isConfirming = confirmRemoveId === member.connect_id;

                return (
                  <ChatRequestItem key={member.user_id || member.connect_id}>
                    <ChatRequestItemInfo>
                      <ChatRequestItemName style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {showAdminDetails && (
                          <ConnectionIndicator isConnected={status.isOnline} isUnknown={status.isUnknown} />
                        )}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {member.name || 'user'}
                          {member.is_creator ? ' · creator' : ''}
                          {isSelf ? ' · you' : ''}
                        </span>
                      </ChatRequestItemName>
                      {showAdminDetails && member.connect_id && (
                        <ChatRequestItemConnectId>
                          id: {member.connect_id}
                          {' · '}
                          <span style={{ color: status.isOnline ? techColors.textPrimary : '#888' }}>
                            {status.label}
                          </span>
                          {member.joined_at && (
                            <>
                              {' · joined '}
                              {formatDate(member.joined_at)}
                            </>
                          )}
                        </ChatRequestItemConnectId>
                      )}
                    </ChatRequestItemInfo>
                    <ChatRequestItemActions>
                      {canRemove && (
                        <ChatRequestActionButton
                          variant="reject"
                          onClick={() => handleRemoveClick(member.connect_id)}
                        >
                          {isConfirming ? t('modals.confirmRemove') : t('common.remove')}
                        </ChatRequestActionButton>
                      )}
                      {canLeave && (
                        <ChatRequestActionButton
                          variant="reject"
                          onClick={onLeaveGroup}
                        >
                          {t('common.leave')}
                        </ChatRequestActionButton>
                      )}
                    </ChatRequestItemActions>
                  </ChatRequestItem>
                );
              })
            ) : (
              <EmptyRequestsMessage>no members</EmptyRequestsMessage>
            )}
          </CompactChatRequestsList>

          {showAdminDetails && pendingInvites.length > 0 && (
            <>
              <div style={sectionTitleStyle}>{t('modals.pendingInvites')}</div>
              <CompactChatRequestsList>
                {pendingInvites.map((invite) => (
                  <ChatRequestItem key={invite.invite_id}>
                    <ChatRequestItemInfo>
                      <ChatRequestItemName>{invite.name || 'user'}</ChatRequestItemName>
                      <ChatRequestItemConnectId>
                        id: {invite.connect_id}
                        {invite.created_at ? ` · sent ${formatDate(invite.created_at)}` : ''}
                        {' · waiting for response'}
                      </ChatRequestItemConnectId>
                    </ChatRequestItemInfo>
                    {isCreator && onCancelInvite && (
                      <ChatRequestItemActions>
                        <ChatRequestActionButton
                          variant="reject"
                          onClick={() => onCancelInvite(invite.invite_id)}
                        >
                          {t('common.cancel')}
                        </ChatRequestActionButton>
                      </ChatRequestItemActions>
                    )}
                  </ChatRequestItem>
                ))}
              </CompactChatRequestsList>
            </>
          )}

          {isCreator && (
            <>
              <button
                type="button"
                onClick={() => setShowAdminPanel((prev) => !prev)}
                style={{
                  marginTop: '16px',
                  width: '100%',
                  padding: '8px',
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '4px',
                  color: techColors.neonCyan,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {showAdminPanel ? t('modals.hideAdmin') : t('modals.showAdmin')}
              </button>

              {showAdminPanel && (
                <>
                  <div style={sectionTitleStyle}>{t('modals.renameGroup')}</div>
                  <ModalFormInput
                    type="text"
                    placeholder={t('modals.groupName')}
                    value={renameName}
                    onChange={(e) => setRenameName(e.target.value)}
                    maxLength={100}
                    $hasError={!!renameError}
                  />
                  <ModalFormStatus>
                    <ModalFormStatusMessage success={false}>{renameError || ''}</ModalFormStatusMessage>
                  </ModalFormStatus>
                  <ChatRequestModalButtons>
                    <ChatRequestModalButton
                      primary
                      onClick={handleRename}
                      disabled={isRenaming || !renameName.trim() || renameName.trim() === title.trim()}
                    >
                      {t('modals.saveName')}
                    </ChatRequestModalButton>
                  </ChatRequestModalButtons>

                  <div style={sectionTitleStyle}>{t('modals.selectMembers')}</div>
                  <ContactSelectList
                    contacts={inviteContacts}
                    selectedConnectIds={selectedInviteConnectIds}
                    onToggleContact={handleToggleInviteContact}
                    emptyMessage={t('chat.noContacts')}
                  />
                  <ModalFormStatus>
                    <ModalFormStatusMessage success={false}>{error || ''}</ModalFormStatusMessage>
                  </ModalFormStatus>
                  <ChatRequestModalButtons>
                    <ChatRequestModalButton
                      primary
                      onClick={handleAdd}
                      disabled={isAdding || selectedInviteConnectIds.length === 0}
                    >
                      {t('modals.sendInvites')}
                    </ChatRequestModalButton>
                  </ChatRequestModalButtons>

                  {onDeleteGroup && (
                    <div style={dangerZoneStyle}>
                      <div style={{ fontSize: '0.75rem', color: techColors.danger, textTransform: 'uppercase', marginBottom: '8px' }}>
                        {t('modals.dangerZone')}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#aaa', marginBottom: '10px' }}>
                        permanently delete this group for all members. this cannot be undone.
                      </div>
                      <ChatRequestActionButton
                        variant="reject"
                        onClick={onDeleteGroup}
                        style={{ width: '100%', justifyContent: 'center' }}
                      >
                        {t('modals.deleteGroupEveryone')}
                      </ChatRequestActionButton>
                    </div>
                  )}
                </>
              )}
            </>
          )}

        </ModalScrollBody>
      </ChatRequestsListModalContent>
    </ChatRequestsListModalOverlay>
  );
};

export default GroupInfoModal;
